import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { Menu, X } from "lucide-react";

export const Logo = ({ className = "h-16 lg:h-24" }: { className?: string }) => (
  <img src="/brand/verge-logo.png" alt="Verge Services — Edge of Excellence" className={`w-auto max-w-full object-contain ${className}`} />
);

const LINKS: [string, string, string][] = [["/", "Home", "home"], ["/directory", "Find Services", "directory"], ["/apply", "For Pros", "apply"]];

const navCls = ({ isActive }: { isActive: boolean }) =>
  `px-1 py-2 text-sm font-semibold transition-colors ${isActive ? "text-white border-b-2 border-white" : "text-[#cdd6ea] hover:text-white"}`;

function AuthButtons({ suffix = "" }: { suffix?: string }) {
  return (
    <>
      <Show when="signed-in">
        <Link to="/app" className="btn btn-primary" data-testid={`nav-dashboard-link${suffix}`}>Dashboard</Link>
        <UserButton />
      </Show>
      <Show when="signed-out">
        <Link to="/sign-in" className="btn btn-outline-light" data-testid={`nav-sign-in-button${suffix}`}>Sign In</Link>
        <Link to="/sign-up" className="btn btn-primary" data-testid={`nav-create-account-button${suffix}`}>Create Account</Link>
      </Show>
    </>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-[var(--navy)] shadow-lg">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2">
        <Link to="/" data-testid="brand-home-link" aria-label="Verge Services home"><Logo /></Link>
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Main">
          {LINKS.map(([to, label, id]) => (
            <NavLink key={id} to={to} end className={navCls} data-testid={`nav-${id}-link`}>{label}</NavLink>
          ))}
          <a href="/#how" className="px-1 py-2 text-sm font-semibold text-[#cdd6ea] hover:text-white" data-testid="nav-how-link">How It Works</a>
        </nav>
        <div className="hidden items-center gap-3 lg:flex"><AuthButtons /></div>
        <button className="p-2 text-white lg:hidden" aria-label="Menu" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="nav-mobile-menu-button">
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>
      {open && (
        <nav className="space-y-1 border-t border-white/10 px-4 pb-4 lg:hidden" aria-label="Mobile" onClick={(e) => { if ((e.target as HTMLElement).closest("a")) setOpen(false); }}>
          {LINKS.map(([to, label, id]) => (
            <Link key={id} to={to} className="block py-2 font-semibold text-white" data-testid={`nav-${id}-link-mobile`}>{label}</Link>
          ))}
          <a href="/#how" className="block py-2 font-semibold text-white" data-testid="nav-how-link-mobile">How It Works</a>
          <div className="flex flex-wrap items-center gap-3 pt-2"><AuthButtons suffix="-mobile" /></div>
        </nav>
      )}
    </header>
  );
}

export function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:bg-white focus:p-2">Skip to content</a>
      <Header />
      <main id="main" className="flex-1">{children}</main>
      <footer className="bg-[var(--navy)] text-[#cdd6ea]">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-between gap-2 px-4 py-5 text-xs">
          <span data-testid="footer-preview-notice">Closed preview · synthetic data only · Stripe test mode (no real charges) · emails only to allow-listed test addresses.</span>
          <span>© Verge Services</span>
        </div>
      </footer>
    </div>
  );
}
